import { reflectKeys } from "@utils/key.reflactor.ts";

export const RESOURCE_TYPE = reflectKeys([
  "FOLDER",
  "FILE",
  "LESSON",
  "MEDIA",
  "QUESTION",
  "RUBRIC",
  "QUIZ",
  "APP",
  "CONTENTMAP",
  "WEBSITE",
]);

export const LESSON_TYPE = reflectKeys(["NEW", "COURSE", "HTML5", "URL"]);
export type LessonType = (typeof LESSON_TYPE)[keyof typeof LESSON_TYPE];

export const LESSON_ACTIVITY_TYPE = reflectKeys(["ACTIVITY", "SECTION", "ACTIVITY_QUIZ"]);

export const ACTIVITY_ICON_NAME = {
  PAGE: "Page32",
  PAGE_COMPLETED_DISABLED: "PageCompletedDisabled32",
  PAGE_COMPLETED: "PageCompleted32",
  PAGE_DISABLED: "PageDisabled32",
  PAGE_NO_ACCESS: "PageNoAccess32",
  PAGE_RESTRICTED: "PageRestricted32",
  PAGE_OPTIONAL: "PageOptional32",
  PAGE_COMPLETED_OPTIONAL: "PageCompletedOptional32",
  PAGE_COMPLETED_DISABLED_OPTIONAL: "PageCompletedDisabledOptional32",
  PAGE_DISABLED_OPTIONAL: "PageDisabledOptional32",
  PAGE_RESTRICTED_OPTIONAL: "PageRestrictedOptional32", // no usage
  PAGE_PREREQ: "PagePrereq32",
  QUIZ_PAGE: "QuizPage32",
  QUIZ_COMPLETED_DISABLED: "QuizPageDisabledCompleted32",
  QUIZ_COMPLETED: "QuizPageCompleted32",
  QUIZ_DISABLED: "QuizPageDisabled32",
  QUIZ_NO_ACCESS: "QuizPageNoAccess32",
  QUIZ_RESTRICTED: "QuizPageRestricted32",
  QUIZ_OPTIONAL: "QuizPageOptional32",
  QUIZ_COMPLETED_OPTIONAL: "QuizPageOptionalCompleted32",
  QUIZ_COMPLETED_DISABLED_OPTIONAL: "QuizPageOptionalCompletedDisabled32",
  QUIZ_DISABLED_OPTIONAL: "QuizPageOptionalDisabled32",
  QUIZ_RESTRICTED_OPTIONAL: "QuizPageRestrictedOptional32", // no usage
  QUIZ_NO_ACCESS_OPTIONAL: "QuizPageOptionalNoAccess32", // no usage
  QUIZ_PAGE_PREREQ: "QuizPagePrereq32",
  PAGINATION: "Pagination32",
};

export const RESOURCE_DOMAIN = {
  MOE: "MOE",
  COMMUNITY: "COMMUNITY",
  PERSONAL: "PERSONAL",
  ALP_TEMPLATE: "ALP_TEMPLATE",
  ASSIGNMENT: "ASSIGNMENT",
};

// TODO: SLS-11043 (DONE), to review the usage
export const RESOURCE_DOMAIN_PATH = {
  PERSONAL: "my-drive",
  MY_LIBRARY: "my-drive",
  MOE: "moe-library",
  MOE_LIBRARY: "moe-library",
  SELF_STUDY: "moe-library",
  COMMUNITY: "community-gallery",
  COMMUNITY_GALLERY: "community-gallery",
  ALP_TEMPLATE: "alp-template",
  ASSIGNMENT: "assignment",
  CLASS_GROUP_RESOURCE: "class-group",
  PERSONAL_LESSON: "my-drive",
};

export const RESOURCE_DOMAIN_PATH_TYPE = {
  [RESOURCE_DOMAIN_PATH.ASSIGNMENT]: RESOURCE_DOMAIN.ASSIGNMENT,
  [RESOURCE_DOMAIN_PATH.CLASS_GROUP_RESOURCE]: RESOURCE_DOMAIN.ASSIGNMENT,
  [RESOURCE_DOMAIN_PATH.MY_LIBRARY]: RESOURCE_DOMAIN.PERSONAL,
  [RESOURCE_DOMAIN_PATH.PERSONAL]: RESOURCE_DOMAIN.PERSONAL,
  [RESOURCE_DOMAIN_PATH.PERSONAL_LESSON]: RESOURCE_DOMAIN.PERSONAL,
  [RESOURCE_DOMAIN_PATH.COMMUNITY_GALLERY]: RESOURCE_DOMAIN.COMMUNITY,
  [RESOURCE_DOMAIN_PATH.ALP_TEMPLATE]: RESOURCE_DOMAIN.ALP_TEMPLATE,
  [RESOURCE_DOMAIN_PATH.MOE_LIBRARY]: RESOURCE_DOMAIN.MOE,
  [RESOURCE_DOMAIN_PATH.SELF_STUDY]: RESOURCE_DOMAIN.MOE,
};

export const QUIZ_TYPE = {
  PRACTICE: "Progressive Quiz",
  TEST_AUTO_RELEASE: "Auto-Graded Quiz",
  TEST_TEACHER_RELEASE: "Teacher-Marked Quiz",
};

export const LESSON_ACTIVITY_COMPONENT_TYPE = reflectKeys([
  "CONTAINER_SINGLE_COLUMN",
  "CONTAINER_TWO_COLUMN",
  "CONTAINER_THREE_COLUMN",
  "CONTAINER_ACCORDION",
  "CONTAINER_ACCORDION_ITEM",
  "CONTAINER_TAB",
  "CONTAINER_TAB_ITEM",
  "COMPONENT_QUESTION",
  "COMPONENT_RICHTEXT",
  "COMPONENT_MEDIAOBJECT",
  "COMPONENT_POLL",
  "COMPONENT_TOPIC",
  "ACTIVITY_QUIZ",
]);
export type LessonActivityComponentType =
  (typeof LESSON_ACTIVITY_COMPONENT_TYPE)[keyof typeof LESSON_ACTIVITY_COMPONENT_TYPE];

export const ZIP_FILE_TYPE = reflectKeys([
  "ASSIGNMENT_RESPONSE",
  "ASSIGNMENT_MARKS",
  "LEARNING_PROGRESS_ASSIGNMENT",
  "LEARNING_PROGRESS_SELF_STUDY",
  "LEARNING_PROGRESS_ALS",
]);

export const LEARNING_MODE_TYPE = reflectKeys(["STANDARD", "DIAGNOSTIC"]);

export const LEARNING_TYPE = {
  CUSTOM: "Custom",
  ACQUISITION: "Acquisition",
  COLLABORATION: "Collaboration",
  DISCUSSION: "Discussion",
  INQUIRY: "Inquiry",
  PRACTICE: "Practice",
  PRODUCTION: "Production",
};

export const LESSON_ACTIVITY_COMPONENT_ERROR_TYPE = reflectKeys([
  "XML_NOT_FOUND",
  "XML_INVALID",
  "QTI_VERSION_MISMATCH",
  "MISSING_INTERACTION",
  "MISSING_RESPONSE_DECLARATION",
  "MISSING_ITEM_BODY",
  "MULTI_INTERACTION_DETECTED",
  "UNSUPPORTED_QUESTION_TYPE",
  "SEND_SQS_MESSAGE_FAILED",
  "QUESTION_CREATE_FAILED",
  "QUESTION_VALIDATION_FAIL",
  "RESOURCE_NOT_FOUND",
]);

export const RESOURCE_STATUS = {
  DRAFT: "Draft",
  PENDING_APPROVAL: "Pending Approval",
  PENDING_UNPUBLISH: "Pending Unpublish",
  APPROVED: "Approved",
  UNPUBLISHED: "Unpublished",
  REJECTED: "Rejected",
  ARCHIVED: "Archived",
};

export const QUIZ_TEST_MODE = reflectKeys([
  "PRACTICE",
  "TEST_AUTO_RELEASE",
  "TEST_TEACHER_RELEASE",
]);

export const LANGUAGE = reflectKeys([
  "EN", // English
  "ZH", // Chinese
  "MS", // Malay
  "TA", // Tamil
  "MULTI",
]);

export const LFA_TEMPERATURE_TYPE = reflectKeys(["LOW", "MEDIUM", "HIGH"]);

export const SUBJECT_LEVEL = {
  STANDARD: "STANDARD",
  FOUNDATIONAL: "FOUNDATIONAL",
  G1: "G1",
  G2: "G2",
  G3: "G3",
  H1: "H1",
  H2: "H2",
  ADULT: "ADULT",
} as const;

export const SUBJECT_LEVEL_SELECTION_ORDER = {
  SUBJECT_FIRST: "subject-first",
  LEVEL_FIRST: "level-first",
} as const;

export type SubjectLevelSelectionOrder =
  (typeof SUBJECT_LEVEL_SELECTION_ORDER)[keyof typeof SUBJECT_LEVEL_SELECTION_ORDER];
